import { forwardRef } from 'react';
import type { CSSProperties } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

import type { IconProps } from './Icon.types';

// Spin animation
const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Base icon styles
const baseIconStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  user-select: none;
  vertical-align: middle;
  line-height: 1;

  /* Size variants */
  &[data-size='xs'] {
    width: 12px;
    height: 12px;
    font-size: 12px;
  }

  &[data-size='sm'] {
    width: 16px;
    height: 16px;
    font-size: 16px;
  }

  &[data-size='md'] {
    width: 20px;
    height: 20px;
    font-size: 20px;
  }

  &[data-size='lg'] {
    width: 24px;
    height: 24px;
    font-size: 24px;
  }

  &[data-size='xl'] {
    width: 32px;
    height: 32px;
    font-size: 32px;
  }

  /* Color variants - using semantic colors from theme */
  &[data-color='inherit'] {
    color: inherit;
  }

  &[data-color='primary'] {
    color: #211c1c;
  }

  &[data-color='secondary'] {
    color: #737373;
  }

  &[data-color='info'] {
    color: #0053CD;
  }

  &[data-color='success'] {
    color: #2B8048;
  }

  &[data-color='warning'] {
    color: #F86C05;
  }

  &[data-color='error'] {
    color: #CA212B;
  }

  &[data-color='muted'] {
    color: #adadb0;
  }
`;

// Material Symbols styled component
const MaterialIcon = styled.span<{ $spin?: boolean; $hasRotate?: boolean }>`
  ${baseIconStyles}

  /* Material Symbols font styling */
  font-family: 'Material Symbols Outlined';
  font-weight: normal;
  font-style: normal;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
  font-feature-settings: 'liga';

  /* Spin animation */
  animation: ${({ $spin }) => ($spin ? `${spin} 1s linear infinite` : 'none')};

  /* Rotation transform */
  transform: ${({ $hasRotate }) => ($hasRotate ? 'rotate(var(--icon-rotate, 0deg))' : 'none')};
`;

// SVG Icon styled component
const SvgIcon = styled.span<{ $spin?: boolean; $hasRotate?: boolean }>`
  ${baseIconStyles}

  /* SVG icon styling */
  width: 100%;
  height: 100%;

  svg {
    display: block;
    width: 100%;
    height: 100%;
    fill: currentColor;
  }

  /* Spin animation */
  animation: ${({ $spin }) => ($spin ? `${spin} 1s linear infinite` : 'none')};

  /* Rotation transform */
  transform: ${({ $hasRotate }) => ($hasRotate ? 'rotate(var(--icon-rotate, 0deg))' : 'none')};
`;

/**
 * Icon component - Flexible icon component supporting Material Symbols and custom SVG
 *
 * @example
 * ```tsx
 * // Using Material Symbols
 * <Icon name="folder" size="md" color="primary" />
 * <Icon name="download" size="lg" />
 * <Icon name="search" />
 *
 * // Using custom SVG
 * <Icon size="md" color="error">
 *   <svg viewBox="0 0 24 24">
 *     <path d="M..." />
 *   </svg>
 * </Icon>
 *
 * // With spin animation
 * <Icon name="refresh" spin />
 *
 * // With rotation
 * <Icon name="arrow_forward" rotate={90} />
 * ```
 */
export const Icon = forwardRef<HTMLSpanElement, IconProps>(
  (
    {
      name,
      children,
      size = 'md',
      color = 'inherit',
      spin: spinProp = false,
      rotate,
      'aria-label': ariaLabel,
      'aria-hidden': ariaHidden = false,
      className,
      style,
      'data-testid': testId,
      dataTestId,
      dataId,
    },
    ref
  ) => {
    // Determine if using Material Symbols or custom SVG
    const isMaterialIcon = !!name && !children;
    const isCustomSvg = !!children;

    // Calculate size
    const sizeValue = typeof size === 'number' ? `${size}px` : undefined;

    // Build inline styles
    const inlineStyles: CSSProperties = {
      ...style,
      ...(sizeValue && {
        width: sizeValue,
        height: sizeValue,
        fontSize: sizeValue,
      }),
      ...(rotate !== undefined &&
        ({
          '--icon-rotate': `${rotate}deg`,
        } as CSSProperties)),
    };

    // Determine if icon should have role="img"
    const shouldHaveRole = isCustomSvg && ariaLabel && !ariaHidden;

    if (isMaterialIcon) {
      return (
        <MaterialIcon
          ref={ref}
          className={className}
          style={inlineStyles}
          data-icon-type="material"
          data-size={typeof size === 'string' ? size : 'custom'}
          data-color={color}
          data-spin={spinProp.toString()}
          data-rotate={rotate !== undefined ? 'true' : undefined}
          $spin={spinProp}
          $hasRotate={rotate !== undefined}
          aria-label={ariaLabel}
          aria-hidden={ariaHidden || (!ariaLabel && !shouldHaveRole) ? true : undefined}
          role={shouldHaveRole ? 'img' : undefined}
          data-testid={dataTestId || testId}
          data-id={dataId}
        >
          {name}
        </MaterialIcon>
      );
    }

    return (
      <SvgIcon
        ref={ref}
        className={className}
        style={inlineStyles}
        data-icon-type="svg"
        data-size={typeof size === 'string' ? size : 'custom'}
        data-color={color}
        data-spin={spinProp.toString()}
        data-rotate={rotate !== undefined ? 'true' : undefined}
        $spin={spinProp}
        $hasRotate={rotate !== undefined}
        aria-label={ariaLabel}
        aria-hidden={ariaHidden || (!ariaLabel && !shouldHaveRole) ? true : undefined}
        role={shouldHaveRole ? 'img' : undefined}
        data-testid={dataTestId || testId}
        data-id={dataId}
      >
        {children}
      </SvgIcon>
    );
  }
);

Icon.displayName = 'Icon';

export default Icon;